// frontend/src/hooks/useOrderStages.ts
//
// Which Workflow Stage tabs of the Orders page the logged-in user may open.
// Admin → everything. Employee → the "stage_<tab>" toggles the admin ticked under
// Manage Permissions → "Order Workflow Stages".

import { useEffect, useState } from 'react';
import apiService from '../services/api';

export const ORDER_STAGE_KEYS = [
  'all', 'pending', 'shipnow', 'print', 'pack', 'handover', 'transit',
  'out_for_delivery', 'delivery', 'cancel', 'ndr', 'reattempt', 'rto',
] as const;

// stages that can also be ACTED on (not only viewed) - same list as the backend StageActionKeys
export const ORDER_STAGE_ACTION_KEYS = [
  'pending', 'shipnow',
  'print_warehouse', 'print_invoice', 'print_manifest', 'print_cancel', 'print_logistics',
] as const;

// action -> the stage whose View permission it needs
export const ORDER_ACTION_STAGE: Record<string, string> = {
  pending: 'pending', shipnow: 'shipnow',
  print_warehouse: 'print', print_invoice: 'print', print_manifest: 'print',
  print_cancel: 'print', print_logistics: 'print',
};

export type OrderStageKey = typeof ORDER_STAGE_KEYS[number];

export const useOrderStageAccess = () => {
  const [loading, setLoading] = useState(true);
  // null = no restriction (admin, or a server that does not know the module yet)
  const [allowed, setAllowed] = useState<Set<string> | null>(null);
  // true = admin saved the stage toggles for this employee and ticked at least one
  const [hasAnyStage, setHasAnyStage] = useState(false);
  // stages the employee may ACT on (Confirm / ShipNow ...). null = admin (everything allowed)
  const [actions, setActions] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        if (!user || user.role !== 'employee') {
          if (!cancelled) setAllowed(null);
          return;
        }
        const res: any = await apiService.getMyPermissions();
        const mod = res?.success ? res?.data?.permissions?.find((p: any) => p.module === 'order_stages') : null;
        if (!mod) {
          if (!cancelled) setAllowed(null); // module not known by the backend → keep old behaviour
          return;
        }
        const perms = (mod.permissions || {}) as any;
        // the backend answered but sent no stage_* flags at all → it is an older backend; do not hide every tab
        if (!ORDER_STAGE_KEYS.some(k => `stage_${k}` in perms)) {
          if (!cancelled) setAllowed(null);
          return;
        }
        const set = new Set<string>();
        ORDER_STAGE_KEYS.forEach(k => { if (perms[`stage_${k}`]) set.add(k); });
        if (!cancelled) setAllowed(set);
        if (!cancelled) setHasAnyStage(perms.stage_configured === true && set.size > 0);
        const acts = new Set<string>();
        ORDER_STAGE_ACTION_KEYS.forEach(k => { if (set.has(ORDER_ACTION_STAGE[k]) && perms[`action_${k}`] === true) acts.add(k); });
        if (!cancelled) setActions(acts);
      } catch {
        // could not load → do not lock people out of the page; the server still enforces the stages
        if (!cancelled) setAllowed(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return { loading, allowed, hasAnyStage, actions };
};